import type { Action } from "../actions.types";
import { ACTION_TYPE_SLUG } from "../actions.constants";

export type OrganizationData = {
  id: number;
  name: string | null;
  programId: number;
};

/**
 * Extracts all organizations from a user's actions where they have org admin access.
 *
 * Checks for action types that indicate org admin access:
 * - org_manage: Primary action type for organization management
 *
 * @param actions - Array of user actions
 * @returns Array of organization data (id and name) for all orgs the user can admin
 */
export function getOrganizationsFromActions(
  actions: Action[]
): OrganizationData[] {
  if (!actions || actions.length === 0) return [];

  return actions
    .filter((action: Action) => {
      const slug = action.action_type?.slug;
      return slug === ACTION_TYPE_SLUG.ORG_MANAGE;
    })
    .map((action) => {
      const targetData = action.target_object_data as {
        id?: number;
        name?: string;
      } | null;
      const bindingData = action.binding_object_data as {
        id?: number;
        name?: string;
      } | null;

      const id = targetData?.id ?? bindingData?.id;
      const name = targetData?.name ?? bindingData?.name ?? null;
      const programId = action.program;

      return { id, name, programId };
    })
    .filter(
      (org): org is OrganizationData => org.id != null && org.programId != null
    )
    .filter((org, index, self) => {
      const key = `${org.programId}-${org.id}`;
      return index === self.findIndex((o) => `${o.programId}-${o.id}` === key);
    });
}
